import React, { useEffect, useState } from 'react';

const DEMAND_MODEL_URL = process.env.REACT_APP_DEMAND_MODEL_URL || 'https://demand-forecast-model.hellocabradar.workers.dev';
const FEATURE_ORDER = [
  ['events', 'Events', '#f6c98d'],
  ['flights', 'Flights', '#b9c8ee'],
  ['precipitation', 'Rain', '#a9d9d0'],
  ['temperature', 'Temp', '#f5b5a7'],
  ['publicHolidays', 'Public holidays', '#b8d8f0'],
  ['specialDays', 'Special days', '#e8c7f0'],
  ['transport', 'Strikes', '#f4a6a6'],
  ['payday', 'Payday', '#f4df91'],
];

const percent = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;
const wholePercent = (value) => `${Math.round(Number(value || 0) * 100)}%`;
const VISIBLE_DAYS = 7;
const dayLabel = (date) => {
  const value = new Date(`${date}T12:00:00Z`);
  const weekday = value.toLocaleDateString('en-GB', { weekday: 'short' });
  return `${weekday} ${date.slice(8, 10)}/${date.slice(5, 7)}`;
};
const conciseWarnings = (warnings) => {
  const unique = new Map();
  for (const warning of warnings) {
    const feature = warning.feature?.startsWith('Flights') ? 'Flights' : warning.feature;
    const key = `${warning.date}:${feature}`;
    if (!unique.has(key)) unique.set(key, { date: warning.date, feature });
  }
  return [...unique.values()];
};
const featureDisplay = (key, value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'None';
  if (key === 'payday') {
    const days = Number(value || 0);
    return days < 0 ? `${Math.abs(days)} days until LWDOM` : `${days} days since LWDOM`;
  }
  return percent(value);
};
const nominalDisplay = (key, features) => {
  const feature = features?.[key];
  if (!feature) return { nominal: 'None', intensity: 'None', uplift: 'None' };
  const nominal = feature.nominal;
  const intensity = feature.intensity;
  const uplift = feature.uplift;
  const none = nominal === null || nominal === undefined || Number(nominal) === 0;
  let nominalText;
  if (none) nominalText = 'None';
  else if (key === 'events') nominalText = `${Number(nominal).toLocaleString('en-GB')} attendance`;
  else if (key === 'flights') nominalText = `${Number(nominal).toLocaleString('en-GB')} flights`;
  else if (key === 'precipitation') nominalText = `${Number(nominal).toFixed(1)}mm`;
  else if (key === 'temperature') nominalText = `${Number(nominal).toFixed(0)}°C min`;
  else if (key === 'transport') nominalText = `Severity ${Math.round(Number(nominal))}`;
  else if (key === 'payday') nominalText = featureDisplay('payday', nominal);
  else nominalText = percent(nominal);
  return {
    nominal: nominalText,
    intensity: none ? 'None' : percent(intensity),
    uplift: none ? 'None' : percent(uplift)
  };
};

export default function WeeklyDemandChart() {
  const [days, setDays] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [expandedDate, setExpandedDate] = useState(null);
  const [focusedSegment, setFocusedSegment] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${DEMAND_MODEL_URL}/demand/read`, { signal: controller.signal })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'Demand forecast read failed.');
        return body;
      })
      .then((body) => {
        if (controller.signal.aborted) return;
        setDays(body.days || []);
        setWarnings(body.warnings || []);
      })
      .catch((fetchError) => {
        if (!controller.signal.aborted) setError(fetchError.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const maxOffset = Math.max(0, days.length - VISIBLE_DAYS);
  const clampedOffset = Math.min(offset, maxOffset);
  const visibleDays = days.slice(clampedOffset, clampedOffset + VISIBLE_DAYS);
  const displayedWarnings = conciseWarnings(warnings);

  return <section className="weekly-demand-panel">
    <div className="live-header"><div><span className="eyebrow">NEXT FOURTEEN DAYS</span><h2>Demand forecast</h2></div><span className="dashboard-toast">{loading ? 'Loading forecast...' : error || (displayedWarnings.length ? `${displayedWarnings.length} missing feature value${displayedWarnings.length === 1 ? '' : 's'}` : '')}</span></div>
    {displayedWarnings.length > 0 && <div className="warning-detail-panel">{displayedWarnings.map((warning, index) => <div className="warning-detail-line" key={`${warning.date}:${warning.feature}`}><span>{index + 1}.</span><span className="mono">{warning.date}</span><span className="warning-detail-msg">{warning.feature} unavailable; using zero.</span></div>)}</div>}
    {error && <div className="empty-state">Forecast unavailable.</div>}
    {!loading && !error && <>
      <div className="demand-chart-legend">{FEATURE_ORDER.map(([, label, color]) => <span key={label}><i style={{ backgroundColor: color }} />{label}</span>)}</div>
      <div className="demand-chart-carousel">
        <button type="button" className="demand-chart-arrow" aria-label="Previous days" disabled={clampedOffset <= 0} onClick={() => setOffset((current) => Math.max(0, Math.min(current, maxOffset) - 1))}>‹</button>
        <div className="demand-chart" aria-label="Stacked demand forecast bar chart">
          {visibleDays.map((day) => <div className="demand-chart-column" key={day.date}>
            <div className="demand-chart-value">{wholePercent(day.demand)}</div>
            <div className="demand-chart-bar" title={`${dayLabel(day.date)}: base ${wholePercent(day.baseline)} / demand ${wholePercent(day.demand)}`}>
              <button type="button" className={`demand-chart-segment baseline${focusedSegment?.date === day.date && focusedSegment.key === 'base' ? ' focused' : ''}`} aria-label={`${dayLabel(day.date)} Base: ${wholePercent(day.baseline)}`} onMouseEnter={() => setFocusedSegment({ date: day.date, key: 'base' })} onMouseLeave={() => setFocusedSegment(null)} onFocus={() => setFocusedSegment({ date: day.date, key: 'base' })} onBlur={() => setFocusedSegment(null)} style={{ height: `${day.baseline * 100}%`, backgroundColor: '#607d9a' }} />
              {FEATURE_ORDER.map(([key, label, color]) => <button key={key} type="button" className={`demand-chart-segment${focusedSegment?.date === day.date && focusedSegment.key === key ? ' focused' : ''}`} aria-label={`${dayLabel(day.date)} ${label}: ${percent(day.features[key].uplift)} uplift`} onMouseEnter={() => setFocusedSegment({ date: day.date, key })} onMouseLeave={() => setFocusedSegment(null)} onFocus={() => setFocusedSegment({ date: day.date, key })} onBlur={() => setFocusedSegment(null)} style={{ height: `${(day.baseline * day.features[key].uplift) * 100}%`, backgroundColor: color }} />)}
            </div>
            {focusedSegment?.date === day.date && <div className="demand-chart-callout">{focusedSegment.key === 'base' ? 'Base' : FEATURE_ORDER.find(([key]) => key === focusedSegment.key)?.[1]}<strong>{focusedSegment.key === 'base' ? wholePercent(day.baseline) : percent(day.features[focusedSegment.key].uplift)}</strong></div>}
            <strong>{dayLabel(day.date)}</strong>
          </div>)}
        </div>
        <button type="button" className="demand-chart-arrow" aria-label="Next days" disabled={clampedOffset >= maxOffset} onClick={() => setOffset((current) => Math.min(maxOffset, Math.min(current, maxOffset) + 1))}>›</button>
      </div>
      <div className="table-panel weekly-demand-details"><div className="table-scroll"><table><thead><tr><th>Date</th><th>Demand</th>{FEATURE_ORDER.map(([, label]) => <th key={label}>{label}</th>)}</tr></thead><tbody>{days.map((day) => <React.Fragment key={day.date}><tr className="weekly-demand-row" onClick={() => setExpandedDate(expandedDate === day.date ? null : day.date)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setExpandedDate(expandedDate === day.date ? null : day.date); }} tabIndex="0" role="button" aria-expanded={expandedDate === day.date}><td className="mono">{dayLabel(day.date)}</td><td><strong>{wholePercent(day.demand)}</strong></td>{FEATURE_ORDER.map(([key]) => <td key={key}>{featureDisplay(key, day.features[key]?.uplift)}</td>)}</tr>{expandedDate === day.date && <tr className="weekly-demand-breakdown"><td colSpan={FEATURE_ORDER.length + 2}><div className="detail-lines"><div className="detail-line detail-line-head"><span></span><strong>Nominal</strong><strong>Intensity</strong><strong>Uplift</strong></div><div className="detail-line"><span>Base</span><strong>{wholePercent(day.baseline)}</strong><strong>-</strong><strong>-</strong></div>{FEATURE_ORDER.map(([key, label]) => { const d = nominalDisplay(key, day.features); return <div className="detail-line" key={key}><span>{label}</span><strong>{d.nominal}</strong><strong>{d.intensity}</strong><strong>{d.uplift}</strong></div>; })}</div></td></tr>}</React.Fragment>)}</tbody></table></div></div>
    </>}
    {!loading && !error && !days.length && <div className="empty-state">No forecast data returned.</div>}
  </section>;
}
